import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { PlugWorking } from '../components/PlugWorking';
import type { OutreachCampaignLive } from '../outreachTypes';
import { SearchHero } from './SearchHero';
import { SearchSteps } from './SearchSteps';
import { SearchMatches } from './SearchMatches';
import { SearchFooter } from './SearchFooter';
import { ReviewEntry } from './ReviewEntry';
import { NoFitState } from './NoFitState';
import { FreePreviewState } from './FreePreviewState';
import { liveMessages as messages } from './liveMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the live screens; each part uses its own slice of it */
import styles from './Live.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SearchViewProps {
  accountId: number;
  live: OutreachCampaignLive;
  canPitch: boolean;
}

/**
 * Plug is searching (design t1): the phases `searching`, `writing` and `review`. In `review` the
 * review entry sits on top of the same steps. Free and the trial see the preview (t6); a find
 * with no good fit shows "Plug keeps looking" (t6).
 */
const SearchViewComponent: React.FC<SearchViewProps> = ({ accountId, live, canPitch }) => {
  const intl = useIntl();
  const { search, song, phase } = live;
  const step = (key: string) => search?.steps.find((item) => item.key === key);
  const noFit =
    search !== null &&
    phase === 'searching' &&
    search.playlistsLeft === null &&
    search.matchCount === 0 &&
    step('scan')?.state === 'done';

  const body = () => {
    if (search === null) {
      return <PlugWorking title={intl.formatMessage(messages.working)} sub={intl.formatMessage(messages.searchBody)} />;
    }
    if (!canPitch) {
      return <FreePreviewState song={song.title} catalogItemUuid={song.catalogItemUuid} search={search} />;
    }
    if (noFit) {
      return <NoFitState song={song.title} scanned={step('scan')?.count ?? null} />;
    }
    return (
      <div className={`${styles.grid2} ${styles.grid2Left}`}>
        <SearchSteps search={search} />
        <SearchMatches
          campaignUuid={live.campaignUuid}
          song={song.title}
          search={search}
          footer={
            <SearchFooter
              accountId={accountId}
              campaignUuid={live.campaignUuid}
              mailbox={live.mailboxAddress}
              canPause={phase !== 'review'}
            />
          }
        />
      </div>
    );
  };

  return (
    <>
      <SearchHero phase={phase} song={song.title} />
      {phase === 'review' && (
        <ReviewEntry campaignUuid={live.campaignUuid} song={song.title} count={step('write')?.count ?? null} />
      )}
      {body()}
    </>
  );
};

export const SearchView = TypedMemo(SearchViewComponent);
